import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import {
  Autocomplete,
  Icon,
  Text,
  Box,
  Spinner,
  InlineStack,
} from "@medentic/polaris-react";
import { SearchIcon } from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { GeoAddress, GeoAddressType } from "@features/events/types";
import { getLocalizedText } from "@shared/lib";


interface CityFieldProps {
  label: string;
  value?: GeoAddress | null;
  onChange: (city: GeoAddress | null) => void;
  error?: string;
  disabled?: boolean;
  requiredIndicator?: boolean;
}

interface GeoCitySearchResult {
  placeId: string;
  text: string;
}

type AutocompleteSection = {
  title: string;
  options: {
    value: string;
    label: string;
  }[];
};

const generateUUID = (): string =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

const getGeoSessionToken = (): string => {
  const SESSION_TOKEN_KEY = "geo_search_session_token";
  let sessionToken = sessionStorage.getItem(SESSION_TOKEN_KEY);
  if (!sessionToken) {
    sessionToken = generateUUID();
    sessionStorage.setItem(SESSION_TOKEN_KEY, sessionToken);
  }
  return sessionToken;
};

export default function CityField({
  label,
  value,
  onChange,
  error,
  disabled = false,
  requiredIndicator = false,
}: CityFieldProps) {
  const { t, i18n } = useTranslation();
  const { GET } = useApiRequest();

  const [inputValue, setInputValue] = useState(
    getLocalizedText(value?.summary, i18n.language)
  );
  const [cityResults, setCityResults] = useState<GeoCitySearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [hasUserTyped, setHasUserTyped] = useState(false);

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchCities = useCallback(
    async (searchTerm: string) => {
      if (!searchTerm.trim() || searchTerm.length < 3) {
        setCityResults([]);
        return;
      }

      setLoading(true);
      try {
        const sessionToken = getGeoSessionToken();
        const endpoint = `/api/metadata/geo/cities?city=${encodeURIComponent(
          searchTerm
        )}&sessionToken=${sessionToken}&limit=10`;

        const results = await GET<GeoCitySearchResult[]>(
          endpoint,
          false,
          false,
          true
        );

        const cities = results || [];
        setCityResults(cities);
      } catch (error) {
        console.error("City search failed:", error);
        setCityResults([]);
      } finally {
        setLoading(false);
      }
    },
    [GET]
  );

  const handleInputChange = useCallback(
    (searchValue: string) => {
      setInputValue(searchValue);
      setHasUserTyped(true);

      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

      if (!searchValue.trim()) {
        setCityResults([]);
        onChange(null);
        setHasUserTyped(false);
        return;
      }

      if (searchValue.trim().length < 3) {
        setCityResults([]);
        return;
      }

      searchTimeoutRef.current = setTimeout(() => {
        searchCities(searchValue);
      }, 500);
    },
    [searchCities, onChange]
  );

  const options = useMemo((): AutocompleteSection[] => {
    if (cityResults.length === 0) {
      return [];
    }

    return [
      {
        title: "",
        options: cityResults.map((city) => ({
          value: city.placeId,
          label: city.text,
        })),
      },
    ];
  }, [cityResults]);

  const updateSelection = useCallback(
    (selected: string[]) => {
      if (selected.length === 0) {
        setSelectedOptions([]);
        setInputValue("");
        onChange(null);
        return;
      }

      const selectedPlaceId = selected[0];
      const selectedCity = cityResults.find(
        (city) => city.placeId === selectedPlaceId
      );

      if (selectedCity) {
        setSelectedOptions(selected);
        setInputValue(selectedCity.text);
        onChange({
          id: 0,
          googlePlaceId: selectedCity.placeId,
          type: GeoAddressType.CITY,
          summary: { [i18n.language]: selectedCity.text },
          city: { [i18n.language]: selectedCity.text },
          country: { [i18n.language]: "" },
          countryISO3: "",
          cityId: null,
          latitude: null,
          longitude: null,
        });
        setHasUserTyped(false);
      }
    },
    [cityResults, onChange, i18n.language]
  );

  useEffect(() => {
    if (!hasUserTyped) {
      const localizedSummary = getLocalizedText(value?.summary, i18n.language);
      if (localizedSummary !== inputValue) {
        setInputValue(localizedSummary);
      }
    }
  }, [value?.summary, inputValue, hasUserTyped, i18n.language]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const textField = useMemo(
    () => (
      <Autocomplete.TextField
        onChange={handleInputChange}
        label={label}
        value={inputValue}
        prefix={<Icon source={SearchIcon} tone="base" />}
        placeholder={t("common.search")}
        autoComplete="off"
        error={error}
        disabled={disabled}
        loading={loading}
        requiredIndicator={requiredIndicator}
      />
    ),
    [handleInputChange, label, inputValue, t, error, disabled, loading, requiredIndicator]
  );

  return (
    <Box>
      <Autocomplete
        textField={textField}
        selected={selectedOptions}
        options={options}
        onSelect={updateSelection}
        emptyState={
          loading ? (
            <Box padding="400">
              <InlineStack align="center" gap="200">
                <Spinner size="small" />
                <Text as="span" tone="subdued">
                  {t("common.searching")}
                </Text>
              </InlineStack>
            </Box>
          ) : (
            <Box padding="400">
              <Text as="span" tone="subdued">
                {inputValue.length < 3
                  ? t("common.typeToSearch")
                  : t("common.typeToSearch")}
              </Text>
            </Box>
          )
        }
      />
    </Box>
  );
}
